import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { GetClientsParams, CreateClientPayload, UpdateClientPayload } from '../../../shared/types';
import {
  getClients,
  getClientById,
  getClientOptions,
  createClient,
  updateClient,
  getStatCounts,
} from '../../../api/clientsApi';
import { useAuth } from '../../../contexts/AuthContext';
import { PERMISSION } from '../../../shared/constants/permissions';

const CLIENTS_KEY = 'clients';
const CLIENT_DETAIL_KEY = 'client-detail';
const STAT_COUNTS_KEY = 'stat-counts';

export function useClients(params: GetClientsParams) {
  return useQuery({
    queryKey: [CLIENTS_KEY, params],
    queryFn: () => getClients(params),
    placeholderData: (previousData) => previousData,
  });
}

export function useClientOptions(search?: string, options?: { projectId?: string; enabled?: boolean }) {
  const { hasPermission } = useAuth();
  const projectId = options?.projectId;

  return useQuery({
    queryKey: [CLIENTS_KEY, 'options', search, projectId],
    queryFn: () => getClientOptions(search, projectId),
    staleTime: 10 * 60 * 1000,
    enabled: options?.enabled ?? hasPermission(PERMISSION.CLIENTS_VIEW),
  });
}

export function useClientDropdown(allLabel: string, opts?: { enabled?: boolean }) {
  const { hasPermission } = useAuth();
  const canListClients = hasPermission(PERMISSION.CLIENTS_VIEW) && (opts?.enabled ?? true);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useClientOptions(search, { enabled: canListClients });

  const options = useMemo(() => {
    const base = [{ label: allLabel, value: '' }];
    if (!data) return base;
    return [
      ...base,
      ...data.map((c: { id: string; name: string; connectorIds?: string[] }) => ({ label: c.name, value: c.id })),
    ];
  }, [data, allLabel]);

  const onSearch = useCallback((keyword: string) => setSearch(keyword), []);

  return { options, isLoading: isLoading && canListClients, onSearch };
}

export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateClientPayload) => createClient(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateClientPayload }) => updateClient(id, payload),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CLIENT_DETAIL_KEY, id] });
    },
  });
}

export function useClientDetail(id: string | null | undefined) {
  return useQuery({
    queryKey: [CLIENT_DETAIL_KEY, id],
    queryFn: () => getClientById(id ?? ''),
    enabled: !!id,
  });
}

export function useStatCounts(tenantId: string | undefined) {
  return useQuery({
    queryKey: [STAT_COUNTS_KEY, tenantId],
    queryFn: () => getStatCounts(tenantId ?? ''),
    enabled: !!tenantId,
  });
}
